import React, { useState } from 'react';
import { Document, Packer, Paragraph, TextRun, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle, ImageRun, Header } from 'docx';
import bcoreLogo from '../../assets/bcore-logo-dark.png';
import { saveAs } from 'file-saver';

const BLANK = '________________________';

const ORIGINATING_ENTITIES = [
  { value: 'Bridge Core LLC', label: 'Bridge Core LLC', state: 'VA', entityType: 'a Virginia Limited Liability Company', address: '12005 Sunrise Valley Dr., Ste. 410', cityStateZip: 'Reston, VA 20191' },
  { value: 'GeoYeti LLC', label: 'GeoYeti LLC', state: 'AK', entityType: 'an Alaska Limited Liability Company', address: '12005 Sunrise Valley Dr., Ste. 410', cityStateZip: 'Reston, VA 20191' },
  { value: 'Teknoluxion Consulting LLC', label: 'Teknoluxion Consulting LLC', state: 'DC', entityType: 'a District of Columbia Limited Liability Company', address: '12005 Sunrise Valley Dr., Ste. 410', cityStateZip: 'Reston, VA 20191' },
  { value: '2 Twelve Solutions Inc', label: '2 Twelve Solutions Inc', state: 'VA', entityType: 'a Virginia Corporation', address: '241 18th Street South Suite 203', cityStateZip: 'Arlington, VA 22202' },
  { value: 'Fuel Consulting LLC', label: 'Fuel Consulting LLC', state: 'VA', entityType: 'a Virginia Limited Liability Company', address: '6607 Mulroy Street', cityStateZip: 'McLean, VA 22101' },
];

function buildNdaDocument(fields, logoData) {
  const co = fields.companyName;
  const origName = fields.originatingEntity || 'Bridge Core LLC';
  const origAddress = fields.origStreet || '12005 Sunrise Valley Dr., Ste. 410';
  const origCityStateZip = fields.origCityStateZip || 'Reston, VA 20191';

  const normalStyle = { font: 'Arial', size: 24 };
  const boldStyle = { ...normalStyle, bold: true };

  const emptyPara = () => new Paragraph({ children: [new TextRun({ ...normalStyle, text: '' })] });

  const bodyPara = (text, opts = {}) => new Paragraph({
    spacing: { after: 120 },
    alignment: opts.alignment || AlignmentType.JUSTIFIED,
    indent: opts.indent ? { left: opts.indent, hanging: opts.hanging || 0 } : undefined,
    children: Array.isArray(text) ? text : [new TextRun({ ...normalStyle, text })],
  });

  const numberedPara = (num, text) => new Paragraph({
    spacing: { after: 120 },
    alignment: AlignmentType.JUSTIFIED,
    indent: { left: 461, hanging: 461 },
    children: Array.isArray(text)
      ? [new TextRun({ ...normalStyle, text: `${num}.\t` }), ...text]
      : [new TextRun({ ...normalStyle, text: `${num}.\t${text}` })],
  });

  const noBorders = {
    top: { style: BorderStyle.NONE, size: 0 },
    bottom: { style: BorderStyle.NONE, size: 0 },
    left: { style: BorderStyle.NONE, size: 0 },
    right: { style: BorderStyle.NONE, size: 0 },
  };

  const twoColTable = (rows, { boldHeader = false, fontSize } = {}) => {
    const baseStyle = fontSize ? { ...normalStyle, size: fontSize } : normalStyle;
    const headerStyle = fontSize ? { ...boldStyle, size: fontSize } : boldStyle;
    return new Table({
      columnWidths: [4680, 4680],
      borders: {
        top: { style: BorderStyle.NONE },
        bottom: { style: BorderStyle.NONE },
        left: { style: BorderStyle.NONE },
        right: { style: BorderStyle.NONE },
        insideHorizontal: { style: BorderStyle.NONE },
        insideVertical: { style: BorderStyle.NONE },
      },
      rows: rows.map((row, i) => new TableRow({
        children: [
          new TableCell({
            width: { size: 4680, type: WidthType.DXA },
            borders: noBorders,
            children: [new Paragraph({
              spacing: { after: 80 },
              children: [new TextRun({ ...(i === 0 && boldHeader ? headerStyle : baseStyle), text: row[0] })],
            })],
          }),
          new TableCell({
            width: { size: 4680, type: WidthType.DXA },
            borders: noBorders,
            children: [new Paragraph({
              spacing: { after: 80 },
              children: [new TextRun({ ...(i === 0 && boldHeader ? headerStyle : baseStyle), text: row[1] })],
            })],
          }),
        ],
      })),
    });
  };

  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: 'Arial', size: 24 },
        },
      },
    },
    sections: [{
      properties: {
        titlePage: true,
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
        },
      },
      headers: logoData ? {
        first: new Header({
          children: [new Paragraph({
            alignment: AlignmentType.CENTER,
            spacing: { after: 240 },
            children: [new ImageRun({
              type: 'png',
              data: logoData,
              transformation: { width: 304, height: 47 },
              altText: { title: 'B/CORE Logo', description: 'Bridge Core logo', name: 'bcore-logo' },
            })],
          })],
        }),
      } : undefined,
      children: [
        // Title
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { before: 240, after: 240 },
          children: [new TextRun({ ...boldStyle, italics: true, underline: { type: 'single' }, text: 'MUTUAL NON-DISCLOSURE AGREEMENT' })],
        }),

        // Preamble
        bodyPara([
          new TextRun({ ...normalStyle, text: 'This Mutual Non-Disclosure Agreement (\u201CAgreement\u201D) is made by and between ' }),
          new TextRun({ ...boldStyle, text: origName }),
          new TextRun({ ...normalStyle, text: [fields.originatingEntityType ? `, ${fields.originatingEntityType}` : '', origAddress ? `, located at ${origAddress}, ${origCityStateZip}` : ''].join('') }),
          new TextRun({ ...normalStyle, text: ' and ' }),
          new TextRun({ ...boldStyle, text: co || '[Counterparty Entity Name]' }),
          new TextRun({ ...normalStyle, text: [fields.entityType ? `, ${fields.entityType}` : '', fields.companyStreet ? `, located at ${[fields.companyStreet, fields.companyCityStateZip].filter(Boolean).join(', ')}` : ''].join('') }),
          new TextRun({ ...normalStyle, text: ', identified below, collectively referred to as the \u201CParties\u201D. For purposes of this Agreement (a) \u201CParties\u201D shall mean, as to any person, its directors, officers, employees, agents and advisors (including, without limitation, financial advisors, attorneys and accountants); and (b) \u201Cperson\u201D shall be broadly interpreted to include, without limitation, any corporation, company, partnership, other entity or individual. For purposes of this Agreement, a Representative of ' }),
          new TextRun({ ...normalStyle, text: co ? `[${co}]` : '[Company]' }),
          new TextRun({ ...normalStyle, text: ' shall include any other entity that, directly or indirectly, is in control of, is controlled by, or is under common control with, ' }),
          new TextRun({ ...normalStyle, text: co ? `[${co}]` : '[Company]' }),
          new TextRun({ ...normalStyle, text: '. For purposes of this Agreement, control (including the terms \u201Ccontrolled by\u201D and \u201Cunder common control with\u201D) means, as applied to any entity, the direct or indirect possession of the power to direct or cause the direction of the management and policies of that entity, whether through ownership of voting securities, by contract, or otherwise.' }),
        ]),

        // Purpose
        bodyPara('The Parties wish to exchange Confidential Information (as defined below) for the following Purposes:'),

        bodyPara([
          new TextRun({ ...normalStyle, italics: true, text: 'To disclose Confidential Information related to capabilities, competencies, past experience, financials, and other business intelligence to enable further discussions about potential collaborative, teaming, project and proposal development opportunities and activities supporting ' }),
          new TextRun({ ...normalStyle, italics: true, text: fields.programName || '[Enter name of Opportunity]' }),
          new TextRun({ ...normalStyle, italics: true, text: '.' }),
        ]),

        bodyPara('The Parties have entered into this Agreement to protect the confidentiality of information in accordance with the following terms:'),

        // Clause 1 - Effective Date
        numberedPara(1, [
          new TextRun({ ...normalStyle, text: `The Effective Date of this Agreement is\t${fields.effectiveDate ? new Date(fields.effectiveDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` }),
        ]),

        // Clause 2 - Confidential Information definition
        numberedPara(2, 'In connection with the Purpose, the Parties may disclose certain information it considers confidential and/or proprietary (\u201CConfidential Information\u201D) to the other Party including, but not limited to, tangible, intangible, visual, electronic, present, or future information such as: (a) trade secrets; (b) financial information, including pricing; (c) technical information, including research, development, procedures, algorithms, data, designs, and know-how; (d) business information, including operations, planning, marketing interests, and products; (e) the terms of any agreement entered into between the Parties and the discussions, negotiations and proposals related thereto; and (f) information acquired during any facilities tours.'),

        // Clause 3 - Duty to protect
        numberedPara(3, 'The Party receiving Confidential Information (\u201CRecipient\u201D) will only have a duty to protect Confidential Information disclosed to it by the other Party (\u201CDiscloser\u201D): (i) if it is clearly and conspicuously marked as \u201Cconfidential\u201D or with a similar designation; or (ii) if it is identified by the Discloser as confidential and/or proprietary before, during, or promptly after presentation or communication; or (iii) if it is disclosed in a manner in which the Discloser reasonably communicated, or the Recipient should reasonably have understood under the circumstances, including without limitation those described in the Section above, that the disclosure should be treated as confidential, whether or not the specific designation \u201Cconfidential\u201D or any similar designation is used. In addition, if disclosed orally or in a form that is not susceptible of being provided with a written legend, is identified as proprietary at the time of initial disclosure and reduced to written form and provided to the Receiving Party within 30 days of initial disclosure. In addition, all notes, memoranda, analyses, compilations, forecasts, studies or other materials prepared by the Receiving Party which contain, reflect or are based upon, in whole or in part, Proprietary Information shall also be considered Proprietary Information.'),

        // Clause 4 - Use restrictions
        numberedPara(4, 'Recipient will use the Confidential Information only for the Purpose described above. A Recipient will use the same degree of care, but no less than a reasonable degree of care, as the Recipient uses with respect to its own information of a similar nature to protect the Confidential Information and to prevent: (i) any use of Confidential Information in violation of this agreement; and/or (ii) communication of Confidential Information to any unauthorized third parties. Confidential Information may only be disseminated to employees, directors, agents or third-party contractors of Recipient with a need to know and who have first signed an agreement with either of the Parties containing confidentiality provisions just as restrictive as those set forth herein. The Receiving Party shall be responsible for any breach of the terms of this Agreement by it or its Representatives.'),

        // Clause 5 - No publicity/copies
        numberedPara(5, 'Each Party agrees that it shall not do the following, except with the advanced review and written approval of the other Party: (a) issue or release any articles, advertising, publicity or other matter relating to this Agreement (including the fact that a meeting or discussion has taken place between the Parties) or mentioning or implying the name of the other Party; or (b) make copies of documents containing Confidential Information.'),

        // Clause 6 - Exceptions
        numberedPara(6, 'This Agreement imposes no obligation upon a Recipient with respect to Confidential Information that'),
        bodyPara('(i) was known to the Recipient before receipt from the Discloser;', { indent: 720 }),
        bodyPara('(ii) becomes publicly available through no fault of the Recipient;', { indent: 720 }),
        bodyPara('(iii) is rightfully received from a third party without a duty of confidentiality;', { indent: 720 }),
        bodyPara('(iv) is independently developed by the Recipient without breach of this Agreement;', { indent: 720 }),
        bodyPara('(v) is disclosed with the Discloser\u2019s prior written approval; or', { indent: 720 }),
        bodyPara('(vi) must be disclosed pursuant to a law, court order, or other governmental demand (\u201CProcess\u201D).', { indent: 720 }),
        bodyPara('When disclosure is required under a Process, the Recipient shall promptly notify the Discloser and shall not disclose the Confidential Information unless:', { indent: 461 }),
        bodyPara('1. the Discloser has sought protective relief from the authority issuing the Process and such request has been denied;', { indent: 720 }),
        bodyPara('2. the Discloser has consulted with the Recipient regarding efforts to resist or narrow the Process;', { indent: 720 }),
        bodyPara('3. the Discloser has consented in writing to the disclosure; or', { indent: 720 }),
        bodyPara('4. the Discloser has taken no action to protect its interests within two (2) business days (unless otherwise negotiated) after receiving notice of the Process.', { indent: 720 }),
        bodyPara('If protective relief is not obtained, the Recipient shall use commercially reasonable efforts to disclose only the portion of the Confidential Information legally required and to ensure that such information receives confidential treatment. A disclosure made in accordance with this Section shall not constitute a breach of this Agreement or create liability for the Recipient.', { indent: 461 }),

        // Clause 7 - Warranties
        numberedPara(7, 'EACH DISCLOSER WARRANTS THAT IT HAS THE RIGHT TO DISCLOSE ITS CONFIDENTIAL INFORMATION. NO OTHER WARRANTIES ARE MADE. ALL CONFIDENTIAL INFORMATION DISCLOSED HEREUNDER IS PROVIDED \u201CAS IS\u201D. No party hereto has an obligation to supply any Proprietary Information hereunder. No party hereto makes any representation or warranty regarding the accuracy, completeness or freedom from defects of any Proprietary Information disclosed or with respect to whether the use of such Proprietary Information infringes rights of others, including, without limitation, intellectual property rights. No party hereto shall be liable for damages of any kind as a result of the other party\u2019s reliance on or use of any Proprietary Information provided hereunder.'),

        // Clause 8 - Term
        numberedPara(8, 'The period for exchange of Confidential Information under this Agreement shall end one (1) year after the effective date of this Agreement unless such period is extended by the written agreement of the parties. Either Party may terminate at any time with thirty (30) days prior written notice to the other party. Notwithstanding the foregoing, this Agreement shall survive with respect to Confidential Information that is disclosed before the effective date of termination.'),

        // Clause 9 - Duration of protection / return
        numberedPara(9, 'Unless the Parties otherwise agree in writing, a Recipient\u2019s duty to protect Confidential Information expires three (3) years from the date of disclosure. All Confidential Information disclosed under this Agreement shall be and remain the property of the Disclosing Party. A Recipient, upon Discloser\u2019s written request, will promptly return all Confidential Information received from the Discloser, together with all copies, or certify in writing that all such Confidential Information and copies thereof have been destroyed. Regardless of whether the Confidential Information is returned or destroyed, the Recipient may retain an archival copy of the Discloser\u2019s Confidential Information in the possession of outside counsel of its own choosing for use solely in the event a dispute arises hereunder and only in connection with such dispute.'),

        // Clause 10 - No obligation
        numberedPara(10, 'This Agreement imposes no obligation on the Parties to exchange Confidential Information, proceed with any business opportunity, or purchase, sell, license, and transfer or otherwise make use of any technology, services or products.'),

        // Clause 11 - No IP rights
        numberedPara(11, 'No Party acquires any intellectual property rights under this Agreement, including, but not limited to, patent, copyright, license and trademark rights).'),

        // Clause 12 - Equitable relief
        numberedPara(12, 'Each Party acknowledges that damages for improper disclosure of Confidential Information may be irreparable; therefore, the injured Party is entitled to seek equitable relief, including injunction and preliminary injunction, in addition to all other remedies available to it. However, under no circumstances shall either party be liable to the other for any consequential, special, punitive, exemplary, incidental, or other indirect damages, of any nature whatsoever, interest or attorney\u2019s fees.'),

        // Clause 13 - Export compliance
        numberedPara(13, 'The Parties and their employees shall not disclose any information furnished hereunder in any manner contrary to the laws and regulations of the United States of America, or any agency thereof, including but not limited to, the Export Administration Regulations of the U.S. Department of Commerce, the International Traffic in Arms Regulations of the U.S. Department of State and the National Industrial Security Program Operating Manual (DOD 5220.22-M).'),

        // Clause 14 - No agency
        numberedPara(14, 'This Agreement does not create any agency or partnership relationship.'),

        // Clause 15 - Assignment
        numberedPara(15, 'Neither Party may assign this Agreement without the prior written consent of the other; however, either Party may assign this Agreement without notice or consent to a successor in interest in connection with a merger, acquisition, corporate reorganization, or sale of substantially all of its assets, provided that such successor assumes all obligations under this Agreement.'),

        // Clause 16 - Entire agreement / severability
        numberedPara(16, 'This Agreement constitutes the entire agreement between the Parties with respect to the subject matter hereof, and supersedes any prior oral or written agreements, and all contemporaneous oral communications. All additions or modifications to this Agreement must be made in writing and must be signed by the Parties. Any failure to enforce a provision of this Agreement shall not constitute a waiver thereof or of any other provision. If any provision of this Agreement is found to be unenforceable, the remainder shall be enforced as fully as possible and the unenforceable provision shall be deemed modified to the limited extent required to permit its enforcement in a manner most closely approximating the intention of the parties as expressed herein.'),

        bodyPara('This Agreement shall be governed by and construed under the laws of the State of Virginia, without regard to its rules for conflict of laws. In the event of any dispute or litigation hereunder, the prevailing party shall be entitled to reimbursement of its costs and reasonable attorney\u2019s fees.'),

        // Clause 17 - Notice contacts
        numberedPara(17, 'The individuals identified below are designated as the primary points of contact for receiving Notice pursuant to this Agreement.'),

        emptyPara(),

        // Notice / Contact table
        twoColTable([
          [origName.toUpperCase(), (co || 'COMPANY').toUpperCase()],
          ['', ''],
          [`Name: ${fields.origNoticesName || BLANK}`, `Name: ${fields.counterNoticesName || BLANK}`],
          [`Title: ${fields.origNoticesTitle || BLANK}`, `Title: ${fields.counterNoticesTitle || BLANK}`],
          [`Phone: ${fields.origNoticesPhone || BLANK}`, `Phone: ${fields.counterNoticesPhone || BLANK}`],
          [`Address: ${fields.origNoticesStreet || origAddress}`, `Address: ${fields.counterNoticesStreet || BLANK}`],
          [fields.origNoticesCityStateZip || origCityStateZip, fields.counterNoticesCityStateZip || BLANK],
          [`Email: ${fields.origNoticesEmail || BLANK}`, `Email: ${fields.counterNoticesEmail || BLANK}`],
        ], { boldHeader: true, fontSize: 20 }),

        emptyPara(),

        // Signature block
        bodyPara('IN WITNESS WHEREOF, the Parties hereto have executed this Agreement as of the date indicated below:'),
        emptyPara(),
        twoColTable([
          [origName.toUpperCase(), (co || 'COMPANY').toUpperCase()],
          ['', ''],
          ['Signature: ____________________', 'Signature: ____________________'],
          [`Name: ${fields.origContactName || BLANK}`, `Name: ${fields.counterContactName || BLANK}`],
          [`Title: ${fields.origContactTitle || BLANK}`, `Title: ${fields.counterContactTitle || BLANK}`],
          ['Date: ____________________', 'Date: ____________________'],
          [`Address: ${fields.origContactStreet || origAddress}`, `Address: ${fields.counterContactStreet || BLANK}`],
          [fields.origContactCityStateZip || origCityStateZip, fields.counterContactCityStateZip || BLANK],
          [`Email: ${fields.origContactEmail || BLANK}`, `Email: ${fields.counterContactEmail || BLANK}`],
        ], { boldHeader: true, fontSize: 20 }),
      ],
    }],
  });

  return doc;
}

function NdaTaGenerator() {
  const [activeCard, setActiveCard] = useState(null);
  const [formData, setFormData] = useState({
    effectiveDate: new Date().toISOString().split('T')[0],
    originatingEntity: 'Bridge Core LLC',
    originatingEntityType: 'a Virginia Limited Liability Company',
    origStreet: '12005 Sunrise Valley Dr., Ste. 410',
    origCityStateZip: 'Reston, VA 20191',
    companyName: '',
    companyShortName: '',
    entityType: '',
    companyStreet: '',
    companyCityStateZip: '',
    programName: '',
    agreementTerm: '',
    // Originator Owner/Signatory Contact (blank — user fills in)
    origContactName: '',
    origContactTitle: '',
    origContactPhone: '',
    origContactStreet: '',
    origContactCityStateZip: '',
    origContactEmail: '',
    // Counterparty Owner/Signatory Contact (blank — user fills in)
    counterContactName: '',
    counterContactTitle: '',
    counterContactPhone: '',
    counterContactStreet: '',
    counterContactCityStateZip: '',
    counterContactEmail: '',
    // Originator Notices Contact (pre-populated)
    origNoticesName: 'Erika Hughes',
    origNoticesTitle: 'B/Core Contracts Administrator',
    origNoticesPhone: '703-867-3679',
    origNoticesStreet: '12005 Sunrise Valley Dr., Ste. 410',
    origNoticesCityStateZip: 'Reston, VA 20191',
    origNoticesEmail: 'contracts@bcore.com',
    // Counterparty Notices Contact (blank)
    counterNoticesName: '',
    counterNoticesTitle: '',
    counterNoticesPhone: '',
    counterNoticesStreet: '',
    counterNoticesCityStateZip: '',
    counterNoticesEmail: '',
  });
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    setFormData((prev) => {
      const next = { ...prev, [field]: val };
      if (field === 'originatingEntity') {
        const match = ORIGINATING_ENTITIES.find((ent) => ent.value === val);
        if (match) {
          next.originatingEntityType = match.entityType;
          next.origStreet = match.address;
          next.origCityStateZip = match.cityStateZip;
        }
      }
      return next;
    });
  };

  const canGenerate = formData.companyName.trim();

  const handleGenerate = async () => {
    if (!canGenerate) return;
    setError('');
    setGenerating(true);
    try {
      let logoData = null;
      try {
        if (bcoreLogo.startsWith('data:')) {
          // CRA inlined the image as a data URI — decode base64
          const base64 = bcoreLogo.split(',')[1];
          const binaryString = atob(base64);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          logoData = bytes;
        } else {
          const logoResp = await fetch(bcoreLogo);
          if (logoResp.ok) {
            const buf = await logoResp.arrayBuffer();
            if (buf.byteLength > 0) {
              logoData = new Uint8Array(buf);
            }
          }
        }
      } catch (logoErr) {
        console.warn('Failed to load logo for NDA:', logoErr);
      }
      const doc = buildNdaDocument(formData, logoData);
      const blob = await Packer.toBlob(doc);
      const fileName = `NDA - BCore - ${formData.companyName.trim()}.docx`;
      saveAs(blob, fileName);
    } catch (err) {
      setError('Failed to generate document: ' + (err.message || 'Unknown error'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="form-page">
      <div className="form-card" style={{ maxWidth: 900 }}>
        <h2 style={{
          fontFamily: 'Kode Mono, monospace',
          fontSize: '18px',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '1px',
          color: '#ffffff',
          marginBottom: '24px',
        }}>
          Document Generator
        </h2>

        {/* Action Cards */}
        <div style={{ display: 'flex', gap: '16px', marginBottom: '32px' }}>
          {/* NDA Card */}
          <button
            onClick={() => setActiveCard(activeCard === 'nda' ? null : 'nda')}
            style={{
              flex: 1,
              padding: '24px',
              background: activeCard === 'nda' ? 'var(--bcore-hover-subtle)' : 'transparent',
              border: activeCard === 'nda' ? '1px solid var(--bcore-accent)' : '1px solid var(--bcore-border, #333)',
              borderRadius: '8px',
              cursor: 'pointer',
              textAlign: 'left',
              transition: 'all 0.15s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--bcore-accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              <span style={{
                fontFamily: 'Kode Mono, monospace',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: '#ffffff',
              }}>
                Generate Generic NDA
              </span>
            </div>
            <span style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '12px',
              color: 'var(--bcore-text-dim)',
            }}>
              Two-way non-disclosure agreement with configurable counterparty details
            </span>
          </button>

          {/* TA Card - Disabled */}
          <div
            style={{
              flex: 1,
              padding: '24px',
              background: 'transparent',
              border: '1px solid var(--bcore-border, #333)',
              borderRadius: '8px',
              opacity: 0.4,
              textAlign: 'left',
              cursor: 'not-allowed',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--bcore-text-dim)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <span style={{
                fontFamily: 'Kode Mono, monospace',
                fontSize: '14px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                color: 'var(--bcore-text-dim)',
              }}>
                Generate Generic Teaming Agreement
              </span>
              <span style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '9px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                padding: '2px 8px',
                borderRadius: '100px',
                background: 'var(--bcore-text-muted)',
                color: 'var(--bcore-bg-primary)',
                fontWeight: 700,
              }}>
                Soon
              </span>
            </div>
            <span style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: '12px',
              color: 'var(--bcore-text-muted)',
            }}>
              Teaming agreement template generator
            </span>
          </div>
        </div>

        {/* NDA Form */}
        {activeCard === 'nda' && (
          <div style={{ borderTop: '1px solid var(--bcore-border, #333)', paddingTop: '24px' }}>
            <div className="form-section" style={{ marginBottom: '28px' }}>
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label">Effective Date of Agreement</label>
                  <input
                    className="form-input"
                    type="date"
                    value={formData.effectiveDate}
                    onChange={handleChange('effectiveDate')}
                  />
                </div>
              </div>
            </div>

            <h3 style={{
              fontFamily: 'Kode Mono, monospace',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#ffffff',
              marginBottom: '20px',
            }}>
              Originating Party Details
            </h3>

            <div className="form-section" style={{ marginBottom: '28px' }}>
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label">Entity Name</label>
                  <select
                    className="form-input"
                    value={formData.originatingEntity}
                    onChange={handleChange('originatingEntity')}
                  >
                    {ORIGINATING_ENTITIES.map((e) => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="form-label">Entity Type</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="e.g., a Virginia LLC"
                    value={formData.originatingEntityType}
                    onChange={handleChange('originatingEntityType')}
                  />
                </div>
              </div>
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
                <div>
                  <label className="form-label">Street Address</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="e.g., 12005 Sunrise Valley Dr., Ste. 410"
                    value={formData.origStreet}
                    onChange={handleChange('origStreet')}
                  />
                </div>
                <div>
                  <label className="form-label">City, State, Zip</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="e.g., Reston, VA 20191"
                    value={formData.origCityStateZip}
                    onChange={handleChange('origCityStateZip')}
                  />
                </div>
              </div>
            </div>

            <h3 style={{
              fontFamily: 'Kode Mono, monospace',
              fontSize: '14px',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '1px',
              color: '#ffffff',
              marginBottom: '20px',
            }}>
              Counterparty Details
            </h3>

            {/* Mandatory Fields */}
            <div className="form-section">
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label">
                    Company Name <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="e.g., Nightwing Intelligence Solutions LLC"
                    value={formData.companyName}
                    onChange={handleChange('companyName')}
                  />
                </div>
              </div>
            </div>

            {/* Counterparty Details (optional) */}
            <div className="form-section" style={{ marginTop: '20px' }}>
              <label className="form-label" style={{ marginBottom: '12px', display: 'block', color: 'var(--bcore-text-dim)', fontSize: '11px' }}>
                COMPANY DETAILS (OPTIONAL)
              </label>
              <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div>
                  <label className="form-label">Entity Type</label>
                  <input className="form-input" type="text" placeholder='e.g., a Florida business' value={formData.entityType} onChange={handleChange('entityType')} />
                </div>
                <div>
                  <label className="form-label">Program Name</label>
                  <input className="form-input" type="text" placeholder="e.g., Olympic Knight Program" value={formData.programName} onChange={handleChange('programName')} />
                </div>
                <div>
                  <label className="form-label">Street Address</label>
                  <input className="form-input" type="text" placeholder="e.g., 22270 Pacific Blvd" value={formData.companyStreet} onChange={handleChange('companyStreet')} />
                </div>
                <div>
                  <label className="form-label">City, State, Zip</label>
                  <input className="form-input" type="text" placeholder="e.g., Dulles, VA 20166" value={formData.companyCityStateZip} onChange={handleChange('companyCityStateZip')} />
                </div>
                <div>
                  <label className="form-label">Agreement Term</label>
                  <input className="form-input" type="text" placeholder='Default: Three (3) years' value={formData.agreementTerm} onChange={handleChange('agreementTerm')} />
                </div>
              </div>
            </div>

            {/* ── NDA Owner/Signatory Contact — Originator left, Counterparty right ── */}
            <h3 style={{ fontFamily: 'Kode Mono, monospace', fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#ffffff', marginTop: '32px', marginBottom: '20px' }}>
              NDA Owner / Signatory Contact
            </h3>

            <div className="form-section" style={{ marginBottom: '0' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Originator column (left) */}
                <div>
                  <label className="form-label" style={{ marginBottom: '12px', display: 'block', color: 'var(--bcore-text-dim)', fontSize: '11px' }}>
                    ORIGINATOR
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label className="form-label">Name</label>
                      <input className="form-input" type="text" placeholder="e.g., Jenny Adams" value={formData.origContactName} onChange={handleChange('origContactName')} />
                    </div>
                    <div>
                      <label className="form-label">Title</label>
                      <input className="form-input" type="text" placeholder="e.g., Contracts Executive" value={formData.origContactTitle} onChange={handleChange('origContactTitle')} />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input className="form-input" type="text" placeholder="e.g., 202-329-8311" value={formData.origContactPhone} onChange={handleChange('origContactPhone')} />
                    </div>
                    <div>
                      <label className="form-label">Street Address</label>
                      <input className="form-input" type="text" placeholder="e.g., 22270 Pacific Blvd" value={formData.origContactStreet} onChange={handleChange('origContactStreet')} />
                    </div>
                    <div>
                      <label className="form-label">City, State, Zip Code</label>
                      <input className="form-input" type="text" placeholder="e.g., Dulles, VA 20166" value={formData.origContactCityStateZip} onChange={handleChange('origContactCityStateZip')} />
                    </div>
                    <div>
                      <label className="form-label">Email Address</label>
                      <input className="form-input" type="text" placeholder="e.g., jenny@company.com" value={formData.origContactEmail} onChange={handleChange('origContactEmail')} />
                    </div>
                  </div>
                </div>
                {/* Counterparty column (right) */}
                <div>
                  <label className="form-label" style={{ marginBottom: '12px', display: 'block', color: 'var(--bcore-text-dim)', fontSize: '11px' }}>
                    COUNTERPARTY
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label className="form-label">Name</label>
                      <input className="form-input" type="text" placeholder="e.g., Jenny Adams" value={formData.counterContactName} onChange={handleChange('counterContactName')} />
                    </div>
                    <div>
                      <label className="form-label">Title</label>
                      <input className="form-input" type="text" placeholder="e.g., Contracts Executive" value={formData.counterContactTitle} onChange={handleChange('counterContactTitle')} />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input className="form-input" type="text" placeholder="e.g., 202-329-8311" value={formData.counterContactPhone} onChange={handleChange('counterContactPhone')} />
                    </div>
                    <div>
                      <label className="form-label">Street Address</label>
                      <input className="form-input" type="text" placeholder="e.g., 22270 Pacific Blvd" value={formData.counterContactStreet} onChange={handleChange('counterContactStreet')} />
                    </div>
                    <div>
                      <label className="form-label">City, State, Zip Code</label>
                      <input className="form-input" type="text" placeholder="e.g., Dulles, VA 20166" value={formData.counterContactCityStateZip} onChange={handleChange('counterContactCityStateZip')} />
                    </div>
                    <div>
                      <label className="form-label">Email Address</label>
                      <input className="form-input" type="text" placeholder="e.g., jenny@company.com" value={formData.counterContactEmail} onChange={handleChange('counterContactEmail')} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{ borderTop: '1px solid var(--bcore-border, #333)', margin: '28px 0' }} />

            {/* ── Notices Contact — Originator left, Counterparty right ── */}
            <h3 style={{ fontFamily: 'Kode Mono, monospace', fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', color: '#ffffff', marginBottom: '20px' }}>
              Notices Contact
            </h3>

            <div className="form-section" style={{ marginBottom: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                {/* Originator column (left) */}
                <div>
                  <label className="form-label" style={{ marginBottom: '12px', display: 'block', color: 'var(--bcore-text-dim)', fontSize: '11px' }}>
                    ORIGINATOR
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label className="form-label">Name</label>
                      <input className="form-input" type="text" value={formData.origNoticesName} onChange={handleChange('origNoticesName')} />
                    </div>
                    <div>
                      <label className="form-label">Title</label>
                      <input className="form-input" type="text" value={formData.origNoticesTitle} onChange={handleChange('origNoticesTitle')} />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input className="form-input" type="text" value={formData.origNoticesPhone} onChange={handleChange('origNoticesPhone')} />
                    </div>
                    <div>
                      <label className="form-label">Street Address</label>
                      <input className="form-input" type="text" value={formData.origNoticesStreet} onChange={handleChange('origNoticesStreet')} />
                    </div>
                    <div>
                      <label className="form-label">City, State, Zip Code</label>
                      <input className="form-input" type="text" value={formData.origNoticesCityStateZip} onChange={handleChange('origNoticesCityStateZip')} />
                    </div>
                    <div>
                      <label className="form-label">Email Address</label>
                      <input className="form-input" type="text" value={formData.origNoticesEmail} onChange={handleChange('origNoticesEmail')} />
                    </div>
                  </div>
                </div>
                {/* Counterparty column (right) */}
                <div>
                  <label className="form-label" style={{ marginBottom: '12px', display: 'block', color: 'var(--bcore-text-dim)', fontSize: '11px' }}>
                    COUNTERPARTY
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div>
                      <label className="form-label">Name</label>
                      <input className="form-input" type="text" placeholder="e.g., Justin Stewart" value={formData.counterNoticesName} onChange={handleChange('counterNoticesName')} />
                    </div>
                    <div>
                      <label className="form-label">Title</label>
                      <input className="form-input" type="text" placeholder="e.g., Director of BD" value={formData.counterNoticesTitle} onChange={handleChange('counterNoticesTitle')} />
                    </div>
                    <div>
                      <label className="form-label">Phone</label>
                      <input className="form-input" type="text" placeholder="e.g., 703-555-1234" value={formData.counterNoticesPhone} onChange={handleChange('counterNoticesPhone')} />
                    </div>
                    <div>
                      <label className="form-label">Street Address</label>
                      <input className="form-input" type="text" placeholder="e.g., 3520 Dixie HWY NE" value={formData.counterNoticesStreet} onChange={handleChange('counterNoticesStreet')} />
                    </div>
                    <div>
                      <label className="form-label">City, State, Zip Code</label>
                      <input className="form-input" type="text" placeholder="e.g., Palm Bay, FL 32905" value={formData.counterNoticesCityStateZip} onChange={handleChange('counterNoticesCityStateZip')} />
                    </div>
                    <div>
                      <label className="form-label">Email Address</label>
                      <input className="form-input" type="text" placeholder="e.g., justin@company.com" value={formData.counterNoticesEmail} onChange={handleChange('counterNoticesEmail')} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div className="form-api-error" style={{ marginTop: '16px' }}>
                {error}
              </div>
            )}

            {/* Generate Button */}
            <div className="form-actions" style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={!canGenerate || generating}
                style={{ minWidth: 180 }}
              >
                {generating ? (
                  <>
                    <span className="spinner" style={{ width: 14, height: 14, marginRight: 8 }} />
                    Generating...
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Generate NDA
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NdaTaGenerator;
